import { available, checkedAddU128, isNonNegativeI64, isU64, unavailable } from './integer.js';
import {
  type OutcomePoolQuoteContext,
  type OutcomeSellQuote,
  quoteOutcomeSellExactIn,
  quoteOutcomeSellFeeExcludedExactIn,
  resolveOutcomePoolQuoteVenue,
} from './outcome-pool.js';
import {
  checkedOutcomePoolQuoteContextFromSnapshot,
  type MarketPoolQuoteSnapshot,
} from './snapshot-context.js';
import type { PoolMathResult, QuoteVenue } from './types.js';

export type PositionValuationVenue = QuoteVenue | 'terminal';
export type PnlViewMode = 'gross' | 'net';

export interface PositionPnlView {
  currentValueD6: bigint;
  remainingCostBasisD6: bigint;
  unrealizedPnlD6: bigint;
}

export interface FullPositionValuationInput {
  context: OutcomePoolQuoteContext;
  outcomeBalanceD6: bigint;
  claimRightsBalanceD6?: bigint;
  remainingCostBasisGrossD6?: bigint;
  remainingCostBasisNetD6: bigint;
}

export interface FullPositionValuation {
  venue: PositionValuationVenue;
  heldOutcomeAmountD6: bigint;
  sellableOutcomeAmountD6: bigint;
  unsellableOutcomeAmountD6: bigint;
  gross: PositionPnlView;
  net: PositionPnlView;
  /** @deprecated Select `net` explicitly. */
  currentValueD6: bigint;
  /** @deprecated Select `net` explicitly. */
  remainingCostBasisNetD6: bigint;
  /** @deprecated Select `net` explicitly. */
  unrealizedPnlD6: bigint;
  sellQuote: OutcomeSellQuote | null;
}

export interface ConsolidatedPoolSnapshotHolding {
  outcomeBalanceD6: bigint;
  claimRightsBalanceD6?: bigint;
  remainingCostBasisGrossD6?: bigint;
  remainingCostBasisNetD6: bigint;
}

export interface ConsolidatedPoolSnapshotMarkHolding extends ConsolidatedPoolSnapshotHolding {
  snapshot: MarketPoolQuoteSnapshot;
  expectedMarket: string;
  expectedOutcomeIndex: number;
  expectedOutcomeMint: string;
}

export interface ConsolidatedPoolSnapshotMark {
  valuationModel: 'consolidated_pool_snapshot_mark';
  gross: PositionPnlView;
  net: PositionPnlView;
  /** @deprecated Select `net` explicitly. */
  markValueD6: bigint;
  /** @deprecated Select `net` explicitly. */
  remainingCostBasisNetD6: bigint;
  /** @deprecated Select `net` explicitly. */
  markUnrealizedPnlD6: bigint;
  poolCount: number;
}

/**
 * Consolidates controlled holdings by outcome pool and quotes each pool once
 * against one coherent snapshot. This mark does not model per-wallet fee
 * rounding or cross-outcome execution order and is not an execution plan.
 */
export function valueConsolidatedPoolSnapshotMark(
  inputs: readonly ConsolidatedPoolSnapshotMarkHolding[],
  quoteUnixTimestampSeconds?: bigint,
): PoolMathResult<ConsolidatedPoolSnapshotMark> {
  const pools = new Map<string, ConsolidatedPoolAccumulator>();
  const snapshotByMarket = new Map<string, { identity: string; fingerprint: string }>();
  const fingerprintBySnapshotIdentity = new Map<string, string>();
  for (const input of inputs) {
    const checkedContext = checkedOutcomePoolQuoteContextFromSnapshot(
      input.snapshot,
      input.expectedMarket,
      input.expectedOutcomeIndex,
      input.expectedOutcomeMint,
    );
    if (!checkedContext.available) return checkedContext;
    const proof = checkedContext.value;
    if (quoteUnixTimestampSeconds !== undefined) {
      proof.context = { ...proof.context, quoteUnixTimestampSeconds };
    }
    const contextFingerprint = outcomePoolQuoteContextFingerprint(proof.context);
    const existingSnapshot = snapshotByMarket.get(proof.marketIdentity);
    if (
      existingSnapshot !== undefined &&
      (existingSnapshot.identity !== proof.marketSnapshotIdentity ||
        existingSnapshot.fingerprint !== proof.marketSnapshotFingerprint)
    ) {
      return unavailable('mixed_pool_snapshot');
    }
    snapshotByMarket.set(proof.marketIdentity, {
      identity: proof.marketSnapshotIdentity,
      fingerprint: proof.marketSnapshotFingerprint,
    });
    const existingFingerprint = fingerprintBySnapshotIdentity.get(proof.marketSnapshotIdentity);
    if (
      existingFingerprint !== undefined &&
      existingFingerprint !== proof.marketSnapshotFingerprint
    ) {
      return unavailable('mixed_pool_snapshot');
    }
    fingerprintBySnapshotIdentity.set(
      proof.marketSnapshotIdentity,
      proof.marketSnapshotFingerprint,
    );
    const existingPool = pools.get(proof.poolIdentity);
    if (existingPool === undefined) {
      pools.set(proof.poolIdentity, {
        context: proof.context,
        contextFingerprint,
        marketSnapshotIdentity: proof.marketSnapshotIdentity,
        marketSnapshotFingerprint: proof.marketSnapshotFingerprint,
        holdings: [input],
      });
      continue;
    }
    if (
      existingPool.contextFingerprint !== contextFingerprint ||
      existingPool.marketSnapshotIdentity !== proof.marketSnapshotIdentity ||
      existingPool.marketSnapshotFingerprint !== proof.marketSnapshotFingerprint
    ) {
      return unavailable('mixed_pool_snapshot');
    }
    existingPool.holdings.push(input);
  }

  let remainingCostBasisGrossD6 = 0n;
  let remainingCostBasisNetD6 = 0n;
  let grossMarkValueD6 = 0n;
  let netMarkValueD6 = 0n;
  for (const pool of pools.values()) {
    const venue = resolveOutcomePoolQuoteVenue(pool.context);
    if (!venue.available) {
      if (venue.reason === 'terminal_losing_outcome') {
        const terminalGrossBasis = sumPoolBasis(pool.holdings, remainingCostBasisGrossD6, 'gross');
        if (!terminalGrossBasis.available) return terminalGrossBasis;
        remainingCostBasisGrossD6 = terminalGrossBasis.value;
        const terminalNetBasis = sumPoolBasis(pool.holdings, remainingCostBasisNetD6, 'net');
        if (!terminalNetBasis.available) return terminalNetBasis;
        remainingCostBasisNetD6 = terminalNetBasis.value;
        continue;
      }
      return venue;
    }
    let sellableOutcomeAmountD6 = 0n;
    for (const holding of pool.holdings) {
      if (
        !isU64(holding.outcomeBalanceD6) ||
        !isNonNegativeI64(grossBasisD6(holding)) ||
        !isNonNegativeI64(holding.remainingCostBasisNetD6)
      ) {
        return unavailable('invalid_amount');
      }
      const nextGrossBasis = checkedAddU128(remainingCostBasisGrossD6, grossBasisD6(holding));
      if (!nextGrossBasis.available) return nextGrossBasis;
      remainingCostBasisGrossD6 = nextGrossBasis.value;
      const nextBasis = checkedAddU128(remainingCostBasisNetD6, holding.remainingCostBasisNetD6);
      if (!nextBasis.available) return nextBasis;
      remainingCostBasisNetD6 = nextBasis.value;
      const sellable = sellableOutcomeAmount(holding, venue.value);
      if (!sellable.available) return sellable;
      sellableOutcomeAmountD6 += sellable.value;
      if (!isU64(sellableOutcomeAmountD6)) return unavailable('invalid_amount');
    }
    if (sellableOutcomeAmountD6 === 0n) continue;
    const quote = quoteOutcomeSellExactIn({
      context: pool.context,
      outcomeAmountInD6: sellableOutcomeAmountD6,
    });
    if (!quote.available) return quote;
    if (quote.value.venue !== venue.value) return unavailable('mixed_pool_snapshot');
    const grossProceeds = feeExcludedGrossProceeds(pool.context, sellableOutcomeAmountD6);
    if (!grossProceeds.available) return grossProceeds;
    const nextGrossValue = checkedAddU128(grossMarkValueD6, grossProceeds.value);
    if (!nextGrossValue.available) return nextGrossValue;
    grossMarkValueD6 = nextGrossValue.value;
    const nextNetValue = checkedAddU128(netMarkValueD6, netProceedsD6(quote.value));
    if (!nextNetValue.available) return nextNetValue;
    netMarkValueD6 = nextNetValue.value;
  }

  return available({
    valuationModel: 'consolidated_pool_snapshot_mark',
    gross: pnlView(grossMarkValueD6, remainingCostBasisGrossD6),
    net: pnlView(netMarkValueD6, remainingCostBasisNetD6),
    markValueD6: netMarkValueD6,
    remainingCostBasisNetD6,
    markUnrealizedPnlD6: netMarkValueD6 - remainingCostBasisNetD6,
    poolCount: pools.size,
  });
}

interface ConsolidatedPoolAccumulator {
  context: OutcomePoolQuoteContext;
  contextFingerprint: string;
  marketSnapshotIdentity: string;
  marketSnapshotFingerprint: string;
  holdings: ConsolidatedPoolSnapshotHolding[];
}

export function valueFullPosition(
  input: FullPositionValuationInput,
): PoolMathResult<FullPositionValuation> {
  if (
    !isU64(input.outcomeBalanceD6) ||
    !isNonNegativeI64(grossBasisD6(input)) ||
    !isNonNegativeI64(input.remainingCostBasisNetD6) ||
    !isU64(input.context.chainClock.slot) ||
    !isU64(input.context.chainClock.unixTimestampSeconds)
  ) {
    return unavailable('invalid_amount');
  }

  const venueResult = resolveOutcomePoolQuoteVenue(input.context);
  if (!venueResult.available) {
    if (venueResult.reason === 'terminal_losing_outcome') {
      return completeValuation(input, 'terminal', 0n, 0n, 0n, null);
    }
    return venueResult;
  }

  if (venueResult.value === 'bonding_curve') return valueBondingCurvePosition(input);
  return valueCpSwapPosition(input);
}

export function calculateUnrealizedPnlD6(
  estimatedSellProceedsD6: bigint,
  remainingCostBasisNetD6: bigint,
): PoolMathResult<bigint> {
  if (!isU64(estimatedSellProceedsD6) || !isNonNegativeI64(remainingCostBasisNetD6)) {
    return unavailable('invalid_amount');
  }
  return available(estimatedSellProceedsD6 - remainingCostBasisNetD6);
}

function valueBondingCurvePosition(
  input: FullPositionValuationInput,
): PoolMathResult<FullPositionValuation> {
  if (input.claimRightsBalanceD6 === undefined) {
    return unavailable('missing_claim_rights_balance');
  }
  if (!isU64(input.claimRightsBalanceD6)) return unavailable('invalid_amount');

  const sellableOutcomeAmountD6 = minimum(input.outcomeBalanceD6, input.claimRightsBalanceD6);
  if (sellableOutcomeAmountD6 === 0n) {
    return completeValuation(input, 'bonding_curve', 0n, 0n, 0n, null);
  }

  const quoteResult = quoteOutcomeSellExactIn({
    context: input.context,
    outcomeAmountInD6: sellableOutcomeAmountD6,
  });
  if (!quoteResult.available) return quoteResult;
  if (quoteResult.value.venue !== 'bonding_curve')
    return unavailable('missing_bonding_curve_state');
  return completeValuation(
    input,
    'bonding_curve',
    sellableOutcomeAmountD6,
    quoteResult.value.grossQuoteOutputD6,
    quoteResult.value.netQuoteOutputD6,
    quoteResult.value,
  );
}

function sellableOutcomeAmount(
  input: ConsolidatedPoolSnapshotHolding,
  venue: QuoteVenue,
): PoolMathResult<bigint> {
  if (venue === 'cp_swap') return available(input.outcomeBalanceD6);
  if (input.claimRightsBalanceD6 === undefined) {
    return unavailable('missing_claim_rights_balance');
  }
  if (!isU64(input.claimRightsBalanceD6)) return unavailable('invalid_amount');
  return available(minimum(input.outcomeBalanceD6, input.claimRightsBalanceD6));
}

function sumPoolBasis(
  holdings: readonly ConsolidatedPoolSnapshotHolding[],
  initialBasisD6: bigint,
  mode: PnlViewMode,
): PoolMathResult<bigint> {
  let basisD6 = initialBasisD6;
  for (const holding of holdings) {
    const holdingBasisD6 =
      mode === 'gross' ? grossBasisD6(holding) : holding.remainingCostBasisNetD6;
    if (!isU64(holding.outcomeBalanceD6) || !isNonNegativeI64(holdingBasisD6)) {
      return unavailable('invalid_amount');
    }
    const nextBasis = checkedAddU128(basisD6, holdingBasisD6);
    if (!nextBasis.available) return nextBasis;
    basisD6 = nextBasis.value;
  }
  return available(basisD6);
}

/** Full deterministic identity for the quote-affecting state of one outcome pool. */
export function outcomePoolQuoteContextFingerprint(context: OutcomePoolQuoteContext): string {
  const venue = context.venue;
  const common = [
    context.poolModel,
    context.chainClock.slot,
    context.chainClock.unixTimestampSeconds,
    context.quoteUnixTimestampSeconds ?? context.chainClock.unixTimestampSeconds,
    venue.lifecycle,
    venue.outcomeIndex,
    venue.winningOutcomeIndex ?? 'null',
    venue.bondingCurveClosed,
    venue.hasCpSwapPool,
  ];
  if (context.poolModel === 'bonding_curve') {
    const { state, fees, conviction, convictionReferenceMarginPpb } = context.bondingCurve;
    return [
      ...common,
      state.virtualQuoteReserveD6,
      state.poolQuoteReserveD6,
      state.initialOutcomeSupplyD6,
      state.poolOutcomeReserveD6,
      fees.buyFeeBps,
      fees.sellBaseFeeBps,
      fees.sellPenaltyFeeBps,
      fees.prizePoolFeeBps,
      fees.minimumQuoteTradeD6,
      convictionReferenceMarginPpb,
      (
        context.bondingCurve.convictionOutcomeIndices ??
        conviction.outcomeConvictionPpb.map((_, index) => index)
      ).join(','),
      conviction.realQuoteReservesD6.join(','),
      conviction.outcomeConvictionPpb.join(','),
      conviction.lastConvictionUpdateUnixSeconds,
      conviction.convictionBaselineReserveQuoteD6,
      conviction.convictionRetentionPerSecondPpb,
      conviction.convictionResolutionThresholdPpb,
    ].join('|');
  }
  if (context.poolModel === 'cp_swap') {
    const pool = context.cpSwap;
    return [
      ...common,
      pool.quoteReserveD6,
      pool.outcomeReserveD6,
      pool.tradeFeeRateMillionths,
      pool.creatorFeeRateMillionths,
      pool.creatorFeeEnabled,
      pool.creatorFeeOn,
      pool.outcomeTokenIndex,
      pool.swapEnabled,
      pool.openTimeUnixSeconds,
    ].join('|');
  }
  return common.join('|');
}

function valueCpSwapPosition(
  input: FullPositionValuationInput,
): PoolMathResult<FullPositionValuation> {
  if (input.context.poolModel !== 'cp_swap') return unavailable('missing_cp_swap_state');
  if (input.outcomeBalanceD6 === 0n) {
    return completeValuation(input, 'cp_swap', 0n, 0n, 0n, null);
  }

  const quoteResult = quoteOutcomeSellExactIn({
    context: input.context,
    outcomeAmountInD6: input.outcomeBalanceD6,
  });
  if (!quoteResult.available) return quoteResult;
  if (quoteResult.value.venue !== 'cp_swap') return unavailable('missing_cp_swap_state');
  const grossQuoteResult = quoteOutcomeSellFeeExcludedExactIn({
    context: input.context,
    outcomeAmountInD6: input.outcomeBalanceD6,
  });
  if (!grossQuoteResult.available) return grossQuoteResult;
  return completeValuation(
    input,
    'cp_swap',
    input.outcomeBalanceD6,
    grossQuoteResult.value,
    quoteResult.value.outputAmountD6,
    quoteResult.value,
  );
}

function completeValuation(
  input: FullPositionValuationInput,
  venue: PositionValuationVenue,
  sellableOutcomeAmountD6: bigint,
  grossCurrentValueD6: bigint,
  netCurrentValueD6: bigint,
  sellQuote: OutcomeSellQuote | null,
): PoolMathResult<FullPositionValuation> {
  return available({
    venue,
    heldOutcomeAmountD6: input.outcomeBalanceD6,
    sellableOutcomeAmountD6,
    unsellableOutcomeAmountD6: input.outcomeBalanceD6 - sellableOutcomeAmountD6,
    gross: pnlView(grossCurrentValueD6, grossBasisD6(input)),
    net: pnlView(netCurrentValueD6, input.remainingCostBasisNetD6),
    currentValueD6: netCurrentValueD6,
    remainingCostBasisNetD6: input.remainingCostBasisNetD6,
    unrealizedPnlD6: netCurrentValueD6 - input.remainingCostBasisNetD6,
    sellQuote,
  });
}

export function selectPositionPnlView(
  valuation: Pick<FullPositionValuation, 'gross' | 'net'>,
  mode: PnlViewMode,
): PositionPnlView {
  return valuation[mode];
}

function feeExcludedGrossProceeds(
  context: OutcomePoolQuoteContext,
  outcomeAmountInD6: bigint,
): PoolMathResult<bigint> {
  return quoteOutcomeSellFeeExcludedExactIn({
    context,
    outcomeAmountInD6,
  });
}

function netProceedsD6(quote: OutcomeSellQuote): bigint {
  return quote.venue === 'bonding_curve' ? quote.netQuoteOutputD6 : quote.outputAmountD6;
}

function pnlView(currentValueD6: bigint, remainingCostBasisD6: bigint): PositionPnlView {
  return {
    currentValueD6,
    remainingCostBasisD6,
    unrealizedPnlD6: currentValueD6 - remainingCostBasisD6,
  };
}

function grossBasisD6(
  input: Pick<FullPositionValuationInput, 'remainingCostBasisGrossD6' | 'remainingCostBasisNetD6'>,
): bigint {
  return input.remainingCostBasisGrossD6 ?? input.remainingCostBasisNetD6;
}

function minimum(left: bigint, right: bigint): bigint {
  return left < right ? left : right;
}
